import { supabase } from './supabase';

/**
 * Google sign-in via ID token (no redirect through *.supabase.co).
 *
 * The redirect flow (signInWithOAuth) makes Google's account chooser say
 * "to continue to ghzrfrwbzlwflqooydam.supabase.co", because that is where the
 * callback lands. Here Google returns an ID token straight to our page (web:
 * Google Identity Services button) or app (iOS: native Google SDK), and we hand
 * it to Supabase with signInWithIdToken. Google then shows getpulsemd.com.
 *
 * Nonce: Google embeds sha256(rawNonce) in the token; Supabase gets rawNonce and
 * checks the hash, so a stolen token can't be replayed.
 *
 * Each path is enabled only when its client ID is set at build time; otherwise
 * callers fall back to the redirect flow, so a missing env var never breaks login.
 */

export const GOOGLE_WEB_CLIENT_ID: string = import.meta.env.VITE_GOOGLE_WEB_CLIENT_ID || '';
export const GOOGLE_IOS_CLIENT_ID: string = import.meta.env.VITE_GOOGLE_IOS_CLIENT_ID || '';

export interface GoogleNonce {
  raw: string;
  hashed: string;
}

export async function createNonce(): Promise<GoogleNonce> {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  const raw = btoa(String.fromCharCode(...bytes)).replace(/[+/=]/g, '');
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(raw));
  const hashed = Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
  return { raw, hashed };
}

export async function signInWithGoogleIdToken(idToken: string, rawNonce: string) {
  const { data, error } = await supabase.auth.signInWithIdToken({
    provider: 'google',
    token: idToken,
    nonce: rawNonce,
  });
  if (error) throw error;
  return data;
}

// ---------- Web: Google Identity Services ----------

interface GsiCredentialResponse {
  credential: string;
}

interface GsiButtonOptions {
  type: 'standard';
  theme: 'outline' | 'filled_blue' | 'filled_black';
  size: 'large' | 'medium' | 'small';
  text: 'signin_with' | 'signup_with' | 'continue_with';
  shape: 'rectangular' | 'pill';
  logo_alignment: 'left' | 'center';
  width?: number;
}

interface GsiId {
  initialize(config: {
    client_id: string;
    callback: (response: GsiCredentialResponse) => void;
    nonce: string;
    ux_mode: 'popup';
    use_fedcm_for_button?: boolean;
    itp_support?: boolean;
  }): void;
  renderButton(parent: HTMLElement, options: GsiButtonOptions): void;
}

declare global {
  interface Window {
    google?: { accounts: { id: GsiId } };
  }
}

const GSI_SRC = 'https://accounts.google.com/gsi/client';
let gsiPromise: Promise<GsiId> | null = null;

export function loadGoogleIdentityServices(): Promise<GsiId> {
  if (window.google?.accounts?.id) return Promise.resolve(window.google.accounts.id);
  if (gsiPromise) return gsiPromise;
  gsiPromise = new Promise<GsiId>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = GSI_SRC;
    script.async = true;
    script.onload = () =>
      window.google?.accounts?.id
        ? resolve(window.google.accounts.id)
        : reject(new Error('Google sign-in failed to load'));
    script.onerror = () => reject(new Error('Google sign-in failed to load'));
    document.head.appendChild(script);
  }).catch((err) => {
    gsiPromise = null; // allow a retry on the next mount
    throw err;
  });
  return gsiPromise;
}

// ---------- iOS: native Google Sign-In SDK ----------

let nativeInitialized = false;

/** Returns Google's ID token after the native account sheet, or throws. */
export async function nativeGoogleIdToken(hashedNonce: string): Promise<string> {
  const { SocialLogin } = await import('@capgo/capacitor-social-login');
  if (!nativeInitialized) {
    await SocialLogin.initialize({
      google: {
        iOSClientId: GOOGLE_IOS_CLIENT_ID,
        // Audience of the ID token must be a client ID Supabase accepts.
        iOSServerClientId: GOOGLE_WEB_CLIENT_ID || undefined,
        mode: 'online',
      },
    });
    nativeInitialized = true;
  }
  const res = await SocialLogin.login({
    provider: 'google',
    options: { scopes: ['email', 'profile'], nonce: hashedNonce },
  });
  const idToken = res.result && 'idToken' in res.result ? res.result.idToken : null;
  if (!idToken) throw new Error('Google did not return an ID token');
  return idToken;
}
